<template>
    <div class="activity-uploader-compact">
        <!-- Config 없음 경고 배너 -->
        <div v-if="showConfigWarning" class="alert-banner alert-warning">
            <i class="fas fa-exclamation-triangle alert-icon"></i>
            <div class="alert-content">
                <strong class="alert-title">활성화된 수집 설정이 없습니다</strong>
                <p class="alert-message">
                    현재 조직에 활성화된 수집 설정이 없어 파일을 업로드할 수 없습니다.<br />
                    먼저 <strong>수집 설정 관리</strong>에서 필요한 설정을 생성하고 활성화해주세요.
                </p>
                <div class="alert-actions">
                    <CarbonButton variant="primary" icon="fas fa-cog" @click="navigateToConfig">
                        수집 설정 관리로 이동
                    </CarbonButton>
                    <CarbonButton icon="fas fa-sync-alt" @click="reloadConfigs">설정 새로고침</CarbonButton>
                </div>
            </div>
        </div>

        <!-- 파일 입력 (숨김) -->
        <input
            type="file"
            class="file-input-hidden"
            id="activityFile"
            ref="fileInput"
            @change="onFileSelect"
            accept=".xlsx,.xls,.csv"
        />

        <!-- 컨텐츠 영역 (전체 너비) -->
        <div class="uploader-content">
            <!-- 데이터 테이블 -->
            <div class="data-table-section-full">
                <!-- 데이터가 있을 때 -->
                <template v-if="parsedData.length > 0">
                    <div class="table-container-wrapper">
                        <div class="table-header-bar">
                            <div class="table-header-left">
                                <div class="table-header-info">
                                    데이터 미리보기
                                    <span class="data-stats">
                                        <span class="stat-badge stat-valid">유효 {{ validRows }}</span>
                                        <span v-if="errorRows > 0" class="stat-badge stat-error"
                                            >오류 {{ errorRows }}</span
                                        >
                                        <span v-if="warningRows > 0" class="stat-badge stat-warning"
                                            >경고 {{ warningRows }}</span
                                        >
                                    </span>
                                </div>
                                <!-- Phase 4: 에러만 보기 필터 -->
                                <div v-if="errorRows > 0" class="filter-checkbox">
                                    <label>
                                        <input type="checkbox" v-model="showErrorsOnly" @change="currentPage = 1" />
                                        <span class="filter-label">
                                            <i class="fas fa-filter"></i>
                                            오류만 보기
                                        </span>
                                    </label>
                                </div>
                            </div>
                            <div class="header-actions">
                                <!-- Phase 1.3: 업로드 상태 선택 -->
                                <CarbonSelect
                                    v-model="uploadDraftStat"
                                    :options="uploadDraftStatOptions"
                                    :disabled="!canUpload || importing"
                                    custom-class="select-draft-stat"
                                />
                                <CarbonButton
                                    variant="upload"
                                    :icon="importing ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-upload-alt'"
                                    :disabled="!canUpload"
                                    :loading="importing"
                                    @click="importData"
                                >
                                    {{ uploadButtonText }}
                                </CarbonButton>
                            </div>
                        </div>

                        <div class="table-scroll-container">
                            <CommonTable width="100%" margin="0" no-border>
                                <template #header-cells>
                                    <th>상태</th>
                                    <th>기간</th>
                                    <th>Scope</th>
                                    <th>카테고리</th>
                                    <th>활동량</th>
                                    <th>단위</th>
                                    <th>시설코드</th>
                                    <th>오류/경고</th>
                                    <th style="width: 100px">작업</th>
                                </template>
                                <template #body>
                                    <tr
                                        v-for="(row, index) in paginatedData"
                                        :key="`row-${currentPage}-${index}`"
                                        :class="{
                                            'row-error': row.errors && row.errors.length > 0,
                                            'row-warning':
                                                (!row.errors || row.errors.length === 0) &&
                                                row.warnings &&
                                                row.warnings.length > 0,
                                        }"
                                    >
                                        <td>
                                            <i
                                                v-if="row.errors && row.errors.length > 0"
                                                class="fas fa-times-circle error-icon"
                                                title="오류"
                                            ></i>
                                            <i
                                                v-else-if="row.warnings && row.warnings.length > 0"
                                                class="fas fa-exclamation-triangle warning-icon"
                                                title="경고"
                                            ></i>
                                            <i v-else class="fas fa-check-circle success-icon" title="정상"></i>
                                        </td>
                                        <td>{{ row.period }}</td>
                                        <td>
                                            <!-- <span class="scope-tag" :class="`scope-${row.scope}`"
                                                >Scope {{ row.scope }}</span
                                            > -->
                                            <span>Scope {{ row.scope }}</span>
                                        </td>
                                        <td>{{ row.category }}</td>
                                        <td class="num-col">{{ formatNumber(row.amount) }}</td>
                                        <td>{{ row.unit }}</td>
                                        <td>{{ getFacilityName(row.facilityIdx) || row.facilityCode || "-" }}</td>
                                        <!-- Phase 1.1: 오류/경고 표시 개선 -->
                                        <td class="error-col">
                                            <!-- 오류 표시 -->
                                            <div v-if="row.errors && row.errors.length > 0" class="error-messages">
                                                <div
                                                    v-for="(error, errorIdx) in row.errors"
                                                    :key="`error-${errorIdx}`"
                                                    class="error-item"
                                                >
                                                    <span class="error-text">{{ error }}</span>
                                                </div>
                                            </div>
                                            <!-- 경고 표시 -->
                                            <div
                                                v-else-if="row.warnings && row.warnings.length > 0"
                                                class="warning-messages"
                                            >
                                                <div
                                                    v-for="(warning, warnIdx) in row.warnings"
                                                    :key="`warning-${warnIdx}`"
                                                    class="warning-item"
                                                >
                                                    <span class="warning-text">{{ warning }}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <!-- Phase 3: 액션 버튼 -->
                                        <td class="action-col">
                                            <div class="action-buttons">
                                                <button
                                                    class="btn-action btn-edit"
                                                    @click.stop="editRow(row)"
                                                    title="수정"
                                                >
                                                    <i class="fas fa-edit"></i>
                                                </button>
                                                <button
                                                    class="btn-action btn-delete"
                                                    @click.stop="deleteRow(row)"
                                                    title="삭제"
                                                >
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </template>
                            </CommonTable>
                        </div>
                    </div>

                    <TablePagination
                        v-if="totalRows > 0"
                        :total-items="totalRows"
                        :current-page.sync="currentPage"
                        :page-size.sync="perPage"
                        :page-size-options="[50, 100, 200, 500]"
                    />
                </template>

                <!-- 데이터가 없을 때: Placeholder -->
                <div v-else class="table-placeholder">
                    <div class="placeholder-content">
                        <i class="fas fa-table placeholder-icon"></i>
                        <h3 class="placeholder-title">활동자료 미리보기</h3>
                        <p class="placeholder-text">
                            파일을 업로드하면<br />
                            활동자료가 여기에 표시됩니다
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <!-- Phase 5: 편집 모달 -->
        <EditRowModal
            :visible="showEditModal"
            :row-data="editingRow"
            :fixed-org-idx="fixedOrgIdx"
            :on-save="handleEditSave"
            @close="showEditModal = false"
        />

        <!-- Phase 3.1: 업로드 결과 상세 모달 -->
        <b-modal v-model="showUploadResultModal" title="업로드 결과" size="lg" hide-footer>
            <div v-if="uploadResult" class="upload-result-modal">
                <div class="result-summary-cards">
                    <div class="result-card success">
                        <div class="result-card-icon">
                            <i class="fas fa-plus-circle"></i>
                        </div>
                        <div class="result-card-content">
                            <div class="result-card-label">신규 생성</div>
                            <div class="result-card-value">{{ uploadResult.created }}건</div>
                        </div>
                    </div>
                    <div class="result-card info">
                        <div class="result-card-icon">
                            <i class="fas fa-sync-alt"></i>
                        </div>
                        <div class="result-card-content">
                            <div class="result-card-label">업데이트</div>
                            <div class="result-card-value">{{ uploadResult.updated }}건</div>
                        </div>
                    </div>
                    <div v-if="uploadResult.failed > 0" class="result-card error">
                        <div class="result-card-icon">
                            <i class="fas fa-times-circle"></i>
                        </div>
                        <div class="result-card-content">
                            <div class="result-card-label">실패</div>
                            <div class="result-card-value">{{ uploadResult.failed }}건</div>
                        </div>
                    </div>
                </div>

                <div v-if="uploadResult.errors && uploadResult.errors.length > 0" class="result-errors">
                    <h6 class="result-section-title">
                        <i class="fas fa-exclamation-triangle"></i>
                        실패 항목 상세
                    </h6>
                    <div class="error-list">
                        <div v-for="(err, idx) in uploadResult.errors.slice(0, 10)" :key="idx" class="error-list-item">
                            <div class="error-list-header">
                                <span class="error-number">#{{ idx + 1 }}</span>
                                <span class="error-message">{{ err.error }}</span>
                            </div>
                            <div v-if="err.data" class="error-data">
                                <span>기간: {{ err.data.period }}</span>
                                <span>Scope: {{ err.data.scope }}</span>
                                <span>카테고리: {{ err.data.category }}</span>
                                <span>활동량: {{ err.data.amount }}</span>
                            </div>
                        </div>
                        <div v-if="uploadResult.errors.length > 10" class="error-more">
                            외 {{ uploadResult.errors.length - 10 }}건의 오류가 더 있습니다.
                        </div>
                    </div>
                </div>

                <div class="result-info">
                    <i class="fas fa-info-circle"></i>
                    <span
                        >업로드 상태: <strong>{{ uploadResult.draftStat === "ACTIVE" ? "확정" : "초안" }}</strong></span
                    >
                </div>
            </div>

            <div class="modal-footer-actions">
                <CarbonButton variant="primary" @click="showUploadResultModal = false">확인</CarbonButton>
            </div>
        </b-modal>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import carbonApi from "@/submodules/carbon-plugin/api";
import CarbonCodes from "@/submodules/carbon-plugin/codes";
import CommonTable from "../../common/CommonTable.vue";
import TablePagination from "../../common/TablePagination.vue";
import EditRowModal from "../modals/EditRowModal.vue"; // Phase 5: 편집 모달
import CarbonButton from "../../common/CarbonButton.vue";
import CarbonSelect from "../../common/CarbonSelect.vue";

export default {
    name: "ActivityUploader",
    components: {
        CommonTable,
        TablePagination,
        EditRowModal, // Phase 5
        CarbonButton,
        CarbonSelect,
    },
    props: {
        loginUser: {
            type: Object,
            default: () => ({}),
        },
        /** 스텝 컨텍스트: 기간 고정 (YYYYMM) - 드롭다운에서 스텝 클릭 시 업로드 데이터에 적용 */
        fixedPeriod: {
            type: [String, Number],
            default: null,
        },
        /** 스텝 컨텍스트: 조직 고정 (orgIdx) */
        fixedOrgIdx: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            selectedFile: null,
            parsedData: [],
            importing: false,
            currentOrg: null,
            // Phase 1: 시설 및 카테고리 매핑
            facilityMap: {}, // { 'FAC-001': 1, 'FAC-002': 2, ... }
            facilityIdxToCodeMap: {}, // { 1: 'FAC-001', 2: 'FAC-002', ... } 역방향 매핑
            facilities: [], // 전체 시설 목록
            categoryMap: {}, // { '고정 연소': 1, '이동 연소': 2, ... }
            categories: [], // 전체 카테고리 목록
            // Phase 1.1: Config 검증
            configs: [], // 활성 수집 설정 목록
            configsLoaded: false, // Config 로드 완료 여부
            // Phase 4: 페이지네이션 및 필터
            currentPage: 1,
            perPage: 50,
            showErrorsOnly: false, // 에러만 보기 필터
            // Phase 5: 편집 모달
            showEditModal: false,
            editingRow: null,
            editingRowIndex: null,
            // Phase 1.3: 업로드 상태 선택 (백엔드: DRAFT, ACTIVE, INACTIVE 대문자 필수)
            uploadDraftStat: "DRAFT",
            // Phase 3.1: 업로드 결과 모달
            showUploadResultModal: false,
            uploadResult: null,
        };
    },
    computed: {
        validRows() {
            return this.parsedData.filter((row) => !row.errors || row.errors.length === 0).length;
        },
        errorRows() {
            return this.parsedData.filter((row) => row.errors && row.errors.length > 0).length;
        },
        warningRows() {
            return this.parsedData.filter((row) => row.warnings && row.warnings.length > 0).length;
        },
        // Phase 4: 필터링된 데이터
        filteredData() {
            if (this.showErrorsOnly) {
                return this.parsedData.filter((row) => row.errors && row.errors.length > 0);
            }
            return this.parsedData;
        },
        // Phase 4: 페이지네이션된 데이터
        paginatedData() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.filteredData.slice(start, end);
        },
        // Phase 4: 총 행 수
        totalRows() {
            return this.filteredData.length;
        },
        // Config 경고 표시 여부
        showConfigWarning() {
            return this.configsLoaded && this.configs.length === 0;
        },
        // 업로드 시 선택 가능한 draftStat 옵션 (DRAFT, ACTIVE만 - 업로드용)
        uploadDraftStatOptions() {
            const list = CarbonCodes.draftStat.getList();
            const uploadValues = ["DRAFT", "ACTIVE"];
            const filtered = list.filter((o) => uploadValues.includes(o.value));
            return filtered.length > 0 ? filtered : list;
        },
        // 업로드 가능 여부
        canUpload() {
            return this.parsedData.length > 0 && this.errorRows === 0 && !this.importing && this.configs.length > 0;
        },
        // 업로드 버튼 텍스트
        uploadButtonText() {
            if (this.importing) return "서버 업로드 중...";
            if (this.configs.length === 0) return "설정 없음 - 업로드 불가";
            if (this.errorRows > 0) return "오류 수정 필요";
            if (this.parsedData.length === 0) return "서버에 업로드";
            return "서버에 업로드";
        },
    },
    watch: {
        // 오류만 보기 상태에서 모든 오류가 해결되면 자동으로 필터 해제
        errorRows(newValue, oldValue) {
            if (this.showErrorsOnly && newValue === 0 && oldValue > 0 && this.parsedData.length > 0) {
                this.showErrorsOnly = false;
                this.currentPage = 1;

                this.showToast("모든 오류가 해결되었습니다. 전체 목록을 표시합니다.", "success", "오류 해결 완료");
            }
        },
    },
    mounted() {
        this.loadActiveSettings();
    },
    methods: {
        async loadActiveSettings() {
            try {
                // fixedOrgIdx가 있으면(드롭다운 스텝 컨텍스트) 해당 조직 사용
                if (this.fixedOrgIdx != null) {
                    this.currentOrg = Number(this.fixedOrgIdx);
                } else {
                    const orgRes = await carbonApi.organization.getOrgs();
                    const orgData = orgRes.data.data || orgRes.data;

                    if (orgData && orgData.organizations && orgData.organizations.length > 0) {
                        this.currentOrg = orgData.organizations[0].orgIdx;
                    }
                }

                // Phase 1: 시설 목록 로드 및 매핑 생성
                await this.loadFacilities();

                // Phase 1: 카테고리 목록 로드 및 매핑 생성
                await this.loadCategories();

                // Phase 1.1: 활성 Config 로드
                await this.loadActiveConfigs();
            } catch (error) {
                console.error("[ActivityUploader] 설정 로드 실패:", error);
            }
        },

        // Phase 1: 시설 목록 로드
        async loadFacilities() {
            try {
                const facilityResponse = await carbonApi.utility.getFacilities();
                const facilityData = facilityResponse?.data?.data || facilityResponse?.data;
                this.facilities = facilityData?.facilities || [];

                // facilityCode → facilityIdx 매핑 생성
                this.facilityMap = {};
                // facilityIdx → facilityCode 역방향 매핑 생성
                this.facilityIdxToCodeMap = {};
                this.facilities.forEach((f) => {
                    if (f.facilityCode && f.facilityIdx) {
                        this.facilityMap[f.facilityCode] = f.facilityIdx;
                        this.facilityIdxToCodeMap[f.facilityIdx] = f.facilityCode;
                    }
                });
            } catch (error) {
                console.error("[ActivityUploader] 시설 목록 로드 실패:", error);
            }
        },

        // Phase 1: 카테고리 목록 로드
        async loadCategories() {
            try {
                const response = await carbonApi.operBoundary.getAllCategories();
                const categoryData = response?.data?.data || response?.data;
                this.categories = categoryData?.categories || [];

                // categoryName → categoryIdx 매핑 생성 (공백 제거 버전도 추가)
                this.categoryMap = {};
                this.categories.forEach((cat) => {
                    if (cat.categoryName && cat.categoryIdx) {
                        // 원본 그대로
                        this.categoryMap[cat.categoryName] = cat.categoryIdx;
                        // 공백 제거 버전 (이동 연소 → 이동연소)
                        const nameWithoutSpaces = cat.categoryName.replace(/\s+/g, "");
                        this.categoryMap[nameWithoutSpaces] = cat.categoryIdx;
                    }
                });
            } catch (error) {
                console.error("[ActivityUploader] 카테고리 목록 로드 실패:", error);
            }
        },

        // Phase 1.1: 활성 Config 목록 로드
        async loadActiveConfigs() {
            try {
                if (!this.currentOrg) {
                    console.warn("[ActivityUploader] currentOrg가 없어 Config를 로드할 수 없습니다");
                    return;
                }

                // const response = await carbonApi.activityConfig.getConfigs({
                //     orgIdx: this.currentOrg,
                //     draftStat: 'ACTIVE' // 활성 설정만 조회
                // });
                const response = await carbonApi.activityConfig.getConfigs({
                    draftStat: "ACTIVE",
                });

                const configData = response?.data?.data || response?.data;
                this.configs = Array.isArray(configData) ? configData : [];
                this.configsLoaded = true;
            } catch (error) {
                console.error("[ActivityUploader] Config 로드 실패:", error);
                this.configs = [];
                this.configsLoaded = false;
            }
        },

        // 설정 페이지로 이동 (더 이상 사용하지 않음 - 탭 구조로 통합됨)
        navigateToConfig() {
            // 탭 구조에서는 이 기능이 필요 없지만, 호환성을 위해 유지
            this.showToast(
                "활성화된 수집 설정이 없습니다. 수집 설정 관리에서 설정을 생성해주세요.",
                "warning",
                "설정 필요",
            );
        },

        // Config 새로고침
        async reloadConfigs() {
            try {
                this.showToast("설정을 다시 불러오는 중...", "info", "설정 새로고침");

                await this.loadActiveConfigs();

                if (this.configs.length > 0) {
                    this.showToast(
                        `${this.configs.length}개의 활성 설정을 찾았습니다.`,
                        "success",
                        "설정 새로고침 완료",
                    );

                    // 기존 데이터가 있으면 재검증
                    if (this.parsedData.length > 0) {
                        this.parsedData.forEach((row) => {
                            // 기존 Config 관련 경고 제거
                            if (row.warnings) {
                                row.warnings = row.warnings.filter(
                                    (w) =>
                                        !w.includes("활성화된 수집 설정이 없습니다") &&
                                        !w.includes("수집 설정이 없습니다"),
                                );
                            }
                            if (row.helpTexts) {
                                row.helpTexts = row.helpTexts.filter((h) => !h.includes("ActivityConfig에서"));
                            }
                            // 재검증
                            this.validateWithConfig(row);
                        });

                        this.showToast("기존 데이터를 다시 검증했습니다.", "success", "재검증 완료");
                    }
                } else {
                    this.showToast("여전히 활성화된 설정이 없습니다.", "warning", "설정 없음");
                }
            } catch (error) {
                console.error("[ActivityUploader] Config 재로드 실패:", error);
                this.showToast("설정을 불러오는 중 오류가 발생했습니다.", "error", "오류");
            }
        },

        // Phase 1.1: Config 기반 row 검증
        validateWithConfig(row) {
            // Config가 로드되지 않았으면 스킵
            if (!this.configsLoaded) {
                return;
            }

            // Config가 없으면 모든 row에 경고 추가
            if (this.configs.length === 0) {
                row.warnings = row.warnings || [];
                if (!row.warnings.includes("활성화된 수집 설정이 없습니다")) {
                    row.warnings.push("활성화된 수집 설정이 없습니다");
                    row.helpTexts = row.helpTexts || [];
                    row.helpTexts.push("ActivityConfig에서 수집 설정을 생성하고 활성화해주세요");
                }
                return;
            }

            // 기간/타입 정규화 후, 해당 row에 맞는 유효한 Config 선택
            // - config.startMonth/endMonth: "YYYY-MM" 또는 "YYYYMM"
            // - row.period: "YYYY-MM" 또는 "YYYYMM" (엑셀/CSV 파싱에 따라 달라질 수 있음)
            const isNil = (v) => v === null || v === undefined;
            const toFiniteNumberOrNull = (v) => {
                if (isNil(v) || v === "") return null;
                const n = Number(v);
                return Number.isFinite(n) ? n : null;
            };
            const normMonthYYYYMM = (m) => {
                if (isNil(m)) return null;
                const s = String(m).replace(/\D/g, "").slice(0, 6);
                return s.length === 6 ? s : null;
            };

            const rowFacilityIdx = toFiniteNumberOrNull(row.facilityIdx);
            const rowCategoryIdx = toFiniteNumberOrNull(row.categoryIdx);
            const period = normMonthYYYYMM(row.period);

            const candidates = this.configs.filter((config) => {
                // facilityIdx: null/undefined이면 전체 시설 설정
                const hasFacility = !isNil(config.facilityIdx);
                const configFacilityIdx = toFiniteNumberOrNull(config.facilityIdx);
                const facilityMatch =
                    !hasFacility ||
                    (configFacilityIdx !== null && rowFacilityIdx !== null && configFacilityIdx === rowFacilityIdx);

                const configCategoryIdx = toFiniteNumberOrNull(config.categoryIdx);
                const categoryMatch =
                    configCategoryIdx !== null && rowCategoryIdx !== null && configCategoryIdx === rowCategoryIdx;

                const start = normMonthYYYYMM(config.startMonth);
                const end = normMonthYYYYMM(config.endMonth);
                const periodMatch = start !== null && period !== null && start <= period && (!end || end >= period);

                return facilityMatch && categoryMatch && periodMatch;
            });

            const validConfig = candidates.sort((a, b) => {
                // 1) 시설 특정 설정 우선 (facilityIdx 있는 것)
                const aSpecific = isNil(a.facilityIdx) ? 0 : 1;
                const bSpecific = isNil(b.facilityIdx) ? 0 : 1;
                if (aSpecific !== bSpecific) return bSpecific - aSpecific;

                // 2) displayOrder 낮은 값 우선
                const aOrder = Number(a.displayOrder ?? 999999);
                const bOrder = Number(b.displayOrder ?? 999999);
                if (aOrder !== bOrder) return aOrder - bOrder;

                // 3) startMonth 최신 우선
                const aStart = normMonthYYYYMM(a.startMonth) || "";
                const bStart = normMonthYYYYMM(b.startMonth) || "";
                return bStart.localeCompare(aStart);
            })[0];

            // Config가 없으면 경고
            if (!validConfig) {
                row.warnings = row.warnings || [];
                row.warnings.push("배출원 설정이 없습니다");
                row.helpTexts = row.helpTexts || [];
                row.helpTexts.push("먼저 ActivityConfig에서 수집 설정을 생성하세요");
                return;
            }

            // Config가 있으면 추가 검증

            // 1. 수집 방법 확인 (SYSTEM 설정 시 경고 - Excel 업로드는 수동 입력)
            if (validConfig.collectionMethod === "SYSTEM") {
                row.warnings = row.warnings || [];
                row.warnings.push(
                    `이 카테고리는 ${this.getMethodLabel(validConfig.collectionMethod)} 방식으로 설정되어 있습니다`,
                );
            }

            // 2. Config 정보 저장 (서버 전송 시 사용, COLLECTION_METHOD: MANUAL|SYSTEM만 허용)
            row.configIdx = validConfig.configIdx;
            row.dataSource = row.dataSource || validConfig.dataSource || null;
            row.collectionMethod = validConfig.collectionMethod === "SYSTEM" ? "SYSTEM" : "MANUAL";
        },

        // 수집 방법 라벨 변환 (공통코드 COLLECTION_METHOD 참조)
        getMethodLabel(method) {
            return CarbonCodes.collectionMethod.getLabel(method) || method;
        },
        triggerFileSelect() {
            this.$refs.fileInput?.click();
        },
        async downloadTemplate() {
            try {
                const period = this.fixedPeriod
                    ? String(this.fixedPeriod).replace(/-/g, "").slice(0, 6)
                    : (() => {
                          const now = new Date();
                          return now.getFullYear().toString() + String(now.getMonth() + 1).padStart(2, "0");
                      })();
                const response = await carbonApi.activityData.exportActivityData({ period });
                const blob = new Blob([response.data], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `활동자료_템플릿_${new Date().toISOString().slice(0, 10)}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                this.showToast("템플릿 다운로드 완료", "success", "다운로드");
            } catch (error) {
                console.error("[ActivityUploader] 템플릿 다운로드 실패:", error);
                this.showToast("템플릿 다운로드 중 오류가 발생했습니다.", "danger", "오류");
            }
        },

        async onFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;

            // 파일 형식 검증
            const allowedExtensions = [".xlsx", ".xls", ".csv"];
            const fileName = file.name.toLowerCase();
            const isValidFile = allowedExtensions.some((ext) => fileName.endsWith(ext));

            if (!isValidFile) {
                this.showToast(
                    "지원하지 않는 파일 형식입니다. (.xlsx, .xls, .csv만 가능)",
                    "warning",
                    "파일 형식 오류",
                );
                this.$refs.fileInput.value = "";
                return;
            }

            this.selectedFile = file;
            this.parsedData = []; // 새 파일 선택 시 기존 파싱 데이터 초기화

            // 부모에게 파일 선택 알림
            this.$emit("file-selected", true);

            // 파일 선택 시 자동으로 파싱
            await this.parseFile(file);
        },

        async parseFile(file) {
            try {
                const data = await this.readFile(file);
                const parsed = this.parseData(data, file.name);

                // Phase 1.1: Config 기반 검증 추가
                if (this.configsLoaded) {
                    parsed.forEach((row) => {
                        this.validateWithConfig(row);
                    });
                }

                this.parsedData = parsed;

                // 경고 메시지 생성
                const warningCount = parsed.filter((row) => row.warnings && row.warnings.length > 0).length;
                let message = `${parsed.length}건 확인 완료. ${this.validRows}건 유효, ${this.errorRows}건 오류`;
                if (warningCount > 0) {
                    message += `, ${warningCount}건 경고`;
                }

                this.showToast(message, this.errorRows > 0 ? "warning" : "success", "파일 확인 완료");
            } catch (error) {
                console.error("파일 파싱 오류:", error);
                this.showToast(
                    "파일 확인 중 오류가 발생했습니다: " + (error.message || "알 수 없는 오류"),
                    "danger",
                    "확인 실패",
                );
            }
        },

        readFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = (e) => {
                    try {
                        const data = e.target.result;
                        const workbook = XLSX.read(data, { type: "binary" });
                        const sheetName = workbook.SheetNames[0];
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                        resolve(jsonData);
                    } catch (error) {
                        reject(error);
                    }
                };

                reader.onerror = reject;
                reader.readAsBinaryString(file);
            });
        },

        parseData(data) {
            if (!data || data.length < 2) {
                throw new Error("데이터가 없거나 헤더가 누락되었습니다.");
            }

            const headers = data[0];
            const rows = data.slice(1);

            const headerMap = {
                period: ["기간", "period", "날짜", "date"],
                scope: ["scope", "스코프", "범위"],
                category: ["카테고리", "카테고리명", "category", "분류"], // 카테고리명 추가
                amount: ["활동량", "amount", "사용량", "량", "수량"],
                unit: ["단위", "unit"],
                facilityCode: ["시설코드", "facilitycode", "facility_code", "시설", "facility"],
            };

            const mappedHeaders = {};
            headers.forEach((header, index) => {
                const cleanHeader = String(header).toLowerCase().trim().replace(/\*/g, "");
                Object.keys(headerMap).forEach((key) => {
                    if (headerMap[key].some((h) => h.toLowerCase().replace(/\*/g, "") === cleanHeader)) {
                        mappedHeaders[key] = index;
                    }
                });
            });

            return rows
                .map((row, index) => {
                    if (!row || row.every((cell) => !cell)) {
                        return null;
                    }

                    let periodValue = String(row[mappedHeaders.period] || "").trim();
                    if (periodValue.includes("-")) {
                        periodValue = periodValue.replace("-", "");
                    }

                    // Phase 1: 시설코드 및 카테고리 매핑
                    const facilityCode = row[mappedHeaders.facilityCode] || "";
                    const categoryName = String(row[mappedHeaders.category] || "").trim();

                    // 카테고리 매핑 (원본 → 공백 제거 순으로 시도)
                    let categoryIdx = this.categoryMap[categoryName];
                    if (!categoryIdx) {
                        const categoryNameWithoutSpaces = categoryName.replace(/\s+/g, "");
                        categoryIdx = this.categoryMap[categoryNameWithoutSpaces];
                    }

                    const parsedRow = {
                        rowNumber: index + 2,
                        period: periodValue,
                        scope: parseInt(row[mappedHeaders.scope]) || null,
                        category: categoryName,
                        categoryIdx: categoryIdx || null, // 매핑 실패 시 null
                        amount: parseFloat(row[mappedHeaders.amount]) || 0,
                        unit: row[mappedHeaders.unit] || "",
                        facilityCode: facilityCode,
                        facilityIdx: this.facilityMap[facilityCode] || null, // Phase 1: 매핑 추가
                        errors: [],
                        helpTexts: [], // Phase 4: 도움말 텍스트
                    };

                    this.validateRow(parsedRow);

                    return parsedRow;
                })
                .filter((row) => row !== null);
        },

        validateRow(row) {
            const errors = [];
            const helpTexts = []; // Phase 4: 도움말 추가

            // 기간 검증
            if (!row.period) {
                errors.push("기간 누락");
                helpTexts.push("예: 202501 (2025년 1월)");
            } else if (!/^\d{6}$/.test(row.period)) {
                errors.push("기간 형식 오류");
                helpTexts.push("YYYYMM 형식으로 입력하세요 (예: 202501)");
            } else {
                helpTexts.push("");
            }

            // Scope 검증
            // if (!row.scope || ![1, 2, 3].includes(row.scope)) {
            //     errors.push("Scope 오류");
            //     helpTexts.push("1(직접배출), 2(간접배출-전기), 3(기타간접배출)");
            // } else {
            //     helpTexts.push("");
            // }

            // 카테고리 검증 (Phase 1: categoryIdx 검증 추가)
            // if (!row.category || row.category.trim() === "") {
            //     errors.push("카테고리 누락");
            //     helpTexts.push("배출 카테고리를 입력하세요");
            // } else if (!row.categoryIdx) {
            //     errors.push(`카테고리 '${row.category}' 미등록`);
            //     helpTexts.push("시스템에 등록된 카테고리를 사용하세요");
            // } else {
            //     helpTexts.push("");
            // }

            // 활동량 검증
            if (row.amount === null || row.amount === undefined || row.amount < 0) {
                errors.push("활동량 오류");
                helpTexts.push("활동량은 0 이상의 숫자여야 합니다");
            } else {
                helpTexts.push("");
            }

            // 단위 검증 (260308: ACTIVITY_UNIT 공통코드만 허용)
            if (!row.unit || row.unit.trim() === "") {
                errors.push("단위 누락");
                helpTexts.push("ACTIVITY_UNIT 공통코드를 선택하세요 (예: kWh, Nm3, L 등)");
            } else {
                const validUnits = CarbonCodes.activityUnit.getList().map((u) => u.value);
                if (!validUnits.includes(row.unit.trim())) {
                    errors.push("단위 미등록");
                    helpTexts.push("ACTIVITY_UNIT 공통코드만 사용 가능합니다. Sm3 등 복합 단위는 지원하지 않습니다.");
                } else {
                    helpTexts.push("");
                }
            }

            // Phase 1: 시설 검증 강화 (facilityIdx 기반)
            if (!row.facilityIdx) {
                if (!row.facilityCode || row.facilityCode.trim() === "") {
                    errors.push("시설코드 누락");
                    helpTexts.push("시설코드를 입력하세요");
                } else {
                    errors.push(`시설코드 '${row.facilityCode}' 미등록`);
                    helpTexts.push("시스템에 등록된 시설코드를 사용하세요");
                }
            } else {
                helpTexts.push("");
            }

            row.errors = errors;
            row.helpTexts = helpTexts; // Phase 4: 도움말 저장
        },

        formatNumber(value) {
            if (typeof value !== "number") return value;
            return new Intl.NumberFormat("ko-KR").format(value);
        },

        /** facilityIdx로 시설명 조회 */
        getFacilityName(facilityIdx) {
            if (facilityIdx == null) return null;
            const facility = this.facilities.find((f) => f.facilityIdx === facilityIdx);
            return facility ? facility.facilityName || facility.facilityCode || null : null;
        },

        async importData() {
            this.importing = true;

            try {
                if (!this.selectedFile) {
                    this.showToast("업로드할 파일을 선택해주세요.", "warning", "업로드 불가");
                    return;
                }

                const validData = this.parsedData.filter((row) => !row.errors || row.errors.length === 0);
                if (validData.length === 0) {
                    this.showToast("업로드할 유효한 데이터가 없습니다.", "warning", "업로드 불가");
                    return;
                }

                // Phase 1.3: draftStat를 데이터에 추가 (백엔드: scope null 미전송, draftStat/collectionMethod 대문자)
                // fixedPeriod가 있으면(드롭다운 스텝 컨텍스트) 모든 행의 period를 고정
                const periodValue = this.fixedPeriod ? String(this.fixedPeriod).replace(/-/g, "").slice(0, 6) : null;
                const dataToUpload = validData.map((row) => {
                    const item = {
                        ...row,
                        period: periodValue || row.period,
                        draftStat: (this.uploadDraftStat || "DRAFT").toUpperCase(),
                        dataSource: row.dataSource || null,
                        collectionMethod: row.collectionMethod || "MANUAL",
                    };
                    // scope: null/undefined면 필드 제거 (백엔드 optional은 undefined만 허용)
                    if (item.scope == null || item.scope === "") {
                        delete item.scope;
                    } else if ([1, 2, 3].includes(Number(item.scope))) {
                        item.scope = Number(item.scope);
                    } else {
                        delete item.scope;
                    }
                    return item;
                });

                // batchUpsertActivityData API 사용
                const response = await carbonApi.activityData.batchUpsertActivityData({
                    activityDataList: dataToUpload,
                    // userIdx: this.loginUser?.userIdx,
                });

                // NOTE:
                // - 현재 apiClient 인터셉터가 4xx/5xx에서 Promise reject가 아니라 err.response를 return함
                // - 따라서 여기서는 status로 성공/실패를 판별해야 함
                if (!response || response.status !== 200) {
                    const errorMsg =
                        response?.data?.error?.message ||
                        response?.data?.message ||
                        "데이터 업로드 중 오류가 발생했습니다.";
                    this.showToast(errorMsg, "danger", "업로드 실패");
                    return;
                }

                // 일부 서버 응답이 200이어도 success=false로 내려오는 케이스 방어
                if (response?.data?.success === false) {
                    const errorMsg =
                        response?.data?.error?.message ||
                        response?.data?.message ||
                        "데이터 업로드 중 오류가 발생했습니다.";
                    this.showToast(errorMsg, "danger", "업로드 실패");
                    return;
                }

                const responseData = response.data?.data || response.data;
                const { created = 0, updated = 0, failed = 0, errors = [] } = responseData;

                const totalProcessed = created + updated + failed;

                // Phase 3.1: 결과 모달 표시
                this.uploadResult = {
                    created,
                    updated,
                    failed,
                    errors,
                    draftStat: this.uploadDraftStat,
                };
                this.showUploadResultModal = true;

                if (failed === 0) {
                    this.clearData();
                }

                this.$emit("data-imported", {
                    totalRows: totalProcessed,
                    successCount: created + updated,
                    errorCount: failed,
                    created,
                    updated,
                    failed,
                    errors,
                    draftStat: this.uploadDraftStat,
                });
            } catch (error) {
                console.error("[ActivityUploader] 데이터 업로드 실패:", error);

                let errorMsg = "데이터 업로드 중 오류가 발생했습니다.";
                if (error.response?.data?.message) {
                    errorMsg = error.response.data.message;
                } else if (error.message) {
                    errorMsg = error.message;
                }
                this.showToast(errorMsg, "danger", "업로드 실패");
            } finally {
                this.importing = false;
            }
        },

        clearData() {
            this.parsedData = [];
            this.selectedFile = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = "";
            }
            // 부모에게 파일 제거 알림
            this.$emit("file-selected", false);
        },

        showToast(message, variant = "info", title = "알림") {
            this.$bvToast.toast(message, { variant, title, toaster: "b-toaster-bottom-right" });
        },

        // Phase 5: 데이터 편집 (모달 사용)
        editRow(row) {
            // 실제 parsedData에서 인덱스 찾기
            const actualIndex = this.parsedData.findIndex((r) => r.rowNumber === row.rowNumber);
            if (actualIndex === -1) {
                this.showToast("편집할 데이터를 찾을 수 없습니다.", "danger", "오류");
                return;
            }

            this.editingRow = { ...row };
            this.editingRowIndex = actualIndex;
            this.showEditModal = true;
        },

        // Phase 5: 편집 저장 핸들러
        handleEditSave(editedData) {
            if (this.editingRowIndex === null) return;

            // parsedData 업데이트
            const updatedRow = {
                ...this.parsedData[this.editingRowIndex],
                period: editedData.period,
                configIdx: editedData.configIdx,
                amount: editedData.amount,
                draftStat: editedData.draftStat,
                dataSource:
                    editedData.dataSource === "" || editedData.dataSource === undefined
                        ? null
                        : editedData.dataSource,
                errors: [],
                helpTexts: [],
            };

            // 유효성 재검증
            this.validateRow(updatedRow);

            // 데이터 업데이트
            this.$set(this.parsedData, this.editingRowIndex, updatedRow);

            this.showToast("데이터가 수정되었습니다. 서버에 업로드하세요.", "success", "편집 완료");

            // 초기화
            this.showEditModal = false;
            this.editingRow = null;
            this.editingRowIndex = null;
        },

        // Phase 3: 데이터 삭제
        deleteRow(row) {
            // 실제 parsedData에서의 인덱스 찾기
            const actualIndex = this.parsedData.findIndex((r) => r.rowNumber === row.rowNumber);

            if (actualIndex === -1) {
                this.showToast("삭제할 데이터를 찾을 수 없습니다.", "danger", "오류");
                return;
            }

            this.$bvModal
                .msgBoxConfirm(
                    `다음 데이터를 삭제하시겠습니까?\n\n기간: ${row.period}\nScope: ${row.scope}\n카테고리: ${row.category}\n활동량: ${row.amount} ${row.unit}`,
                    {
                        title: "데이터 삭제",
                        size: "md",
                        buttonSize: "sm",
                        okVariant: "danger",
                        okTitle: "삭제",
                        cancelTitle: "취소",
                        footerClass: "p-2",
                        hideHeaderClose: false,
                        centered: true,
                    },
                )
                .then((value) => {
                    if (value) {
                        this.parsedData.splice(actualIndex, 1);
                        this.showToast("행이 삭제되었습니다.", "success", "삭제 완료");
                    }
                });
        },
    },
};
</script>

<style scoped>
/* Config 경고 배너 */
.alert-banner {
    display: flex;
    align-items: flex-start;
    padding: 16px 20px;
    margin-bottom: 20px;
    border-radius: 8px;
    border: 1px solid;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.alert-banner.alert-warning {
    background: #fffbeb;
    border-color: #fbbf24;
    color: #92400e;
}

.alert-banner.alert-info {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #1e40af;
}

.alert-banner.alert-info .alert-icon {
    color: #3b82f6;
}

.alert-banner.alert-info .alert-title {
    color: #1e40af;
}

.alert-banner.alert-info .alert-message {
    color: #1e3a8a;
}

.alert-icon {
    font-size: 24px;
    margin-right: 16px;
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
}

.alert-content {
    flex: 1;
}

.alert-title {
    display: block;
    font-size: 16px;
    font-weight: 600;
    color: #92400e;
    margin-bottom: 8px;
}

.alert-message {
    font-size: 14px;
    line-height: 1.6;
    color: #78350f;
    margin: 0 0 12px 0;
}

.alert-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

.alert-actions .btn-compact {
    font-size: 13px;
    padding: 6px 12px;
}

.table-header-info {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}
/* 메인 컨테이너 */
.activity-uploader-compact {
    padding: 0;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
}

/* 빈 상태 섹션 (파일이 없을 때) */
.empty-state-section {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.large-upload-area {
    flex: 1;
    border: 2px dashed #cbd5e1;
    border-radius: 0;
    background: #f8fafc;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    position: relative;
}

.large-upload-area:hover {
    border-color: #52b788;
    background: #f8fafc;
}

.large-upload-area.dragging {
    border-color: #52b788;
    background: #f8fafc;
}

.large-upload-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    text-align: center;
    padding: 40px;
}

.large-upload-icon {
    font-size: 64px;
    color: #cbd5e1;
    margin-bottom: 8px;
}

.large-upload-title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.large-upload-hint {
    font-size: 12px;
    color: #64748b;
    margin: 0;
}

.btn-large-select {
    padding: 8px 16px;
    background: #52b788;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
}

.btn-large-select:hover {
    background: #2d8659;
}

.btn-template-corner {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 8px 16px;
    background: #ffffff;
    color: #64748b;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 10;
    box-shadow: none;
}

.btn-template-corner:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #1e293b;
}

/* 컨텐츠 영역 (전체 너비) */
.uploader-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 600px;
}

/* 파일 표시 영역 CSS 제거됨 (헤더로 이동) */

.progress-bar-wrapper-large {
    margin: 0 16px 16px 16px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 0;
}

.progress-label {
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
}

.progress-bar-large {
    width: 100%;
    height: 8px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 8px;
}

.progress-fill-large {
    height: 100%;
    background: #52b788;
    border-radius: 4px;
    transition: width 0.3s ease;
}

.progress-text-large {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-align: right;
    display: block;
}

/* 테이블 Placeholder */
.table-placeholder {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 500px;
    height: 100%; /* 부모 높이 전체 사용 */
    padding: 16px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
}

.placeholder-content {
    text-align: center;
    padding: 40px;
    margin: auto; /* 정확한 중앙 정렬 */
}

.placeholder-icon {
    font-size: 64px;
    color: #cbd5e1;
    margin-bottom: 16px;
}

.placeholder-title {
    font-size: 14px;
    font-weight: 600;
    color: #475569;
    margin: 0 0 8px 0;
}

.placeholder-text {
    font-size: 12px;
    color: #64748b;
    margin: 0;
    line-height: 1.6;
}

.file-upload-area {
    flex: 1;
    border: 1px solid #cbd5e1;
    border-radius: 0;
    padding: 5px 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 30px;
    display: flex;
    align-items: center;
}

.file-upload-area:hover {
    border-color: #52b788;
    background: #ffffff;
}

.file-upload-area.dragging {
    border-color: #52b788;
    background: #ffffff;
}

.file-upload-area.has-file {
    border-color: #52b788;
    background: #ffffff;
}

.upload-placeholder {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.upload-icon {
    font-size: 16px;
    color: #64748b;
}

.upload-text {
    font-size: 13px;
    font-weight: 500;
    color: #475569;
}

.file-selected-info {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex: 1;
    min-width: 0;
}

.file-icon {
    font-size: 16px;
    color: #52b788;
    flex-shrink: 0;
}

.file-name {
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.btn-change-file {
    padding: 4px 8px;
    background: transparent;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    color: #64748b;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-change-file:hover {
    background: #fee2e2;
    color: #dc2626;
}

.file-input-hidden {
    display: none;
}

.btn-compact:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-template {
    background: #ffffff;
    color: #64748b;
    border: 1px solid #cbd5e1;
}

.btn-template:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
}

.btn-clear {
    background: #ffffff;
    color: #64748b;
    border: 1px solid #cbd5e1;
}

.btn-clear:hover:not(:disabled) {
    background: #fef2f2;
    color: #dc2626;
    border-color: #dc2626;
}

.btn-upload {
    background: #52b788;
    color: #ffffff;
    border: none;
}

.btn-upload:hover:not(:disabled) {
    background: #2d8659;
}

/* 진행률 */
.progress-bar-wrapper {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0;
}

.progress-bar {
    flex: 1;
    height: 4px;
    background: #e2e8f0;
    border-radius: 2px;
    position: relative;
}

.progress-fill {
    height: 100%;
    background: #52b788;
    border-radius: 2px;
    transition: width 0.3s;
}

.progress-text {
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    min-width: 35px;
    text-align: right;
}

/* 오른쪽: 데이터 테이블 섹션 (50%) */
/* 데이터 테이블 섹션 (전체 너비) */
.data-table-section-full {
    flex: 1;
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
    height: 100%;
}

.table-container-wrapper {
    background: #ffffff;
    border: 1px solid #e5e7eb;
    /* border-radius: 14px; */
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 360px); /* 스크롤을 위한 최대 높이 설정 */
    min-height: 500px; /* 최소 높이 보장 */
}

.table-header-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    margin-bottom: 0;
    border-bottom: 1px solid #e2e8f0;
    gap: 12px;
    background: #f8fafc;
    flex-shrink: 0;
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
}

.header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
}

/* Phase 1.3: draftStat 선택 스타일 */
.select-draft-stat {
    padding: 6px 12px;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #1e293b;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
}

.select-draft-stat:hover:not(:disabled) {
    border-color: #52b788;
}

.select-draft-stat:focus:not(:disabled) {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 2px rgba(82, 183, 136, 0.1);
}

.select-draft-stat:disabled {
    background: #f1f5f9;
    color: #94a3b8;
    border-color: #e2e8f0;
    cursor: not-allowed;
    opacity: 0.6;
}

.header-info {
    display: flex;
    gap: 6px;
    align-items: center;
    flex-shrink: 0;
}

.data-count {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.stat-badge {
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
}

.stat-valid {
    background: #f0fdf4;
    color: #52b788;
}

.stat-error {
    background: #fef2f2;
    color: #dc2626;
}

/* Phase 1.1: 경고 배지 */
.stat-warning {
    background: #fffbeb;
    color: #f59e0b;
}

/* 테이블 */
.table-scroll-container {
    flex: 1;
    min-height: 0;
    max-height: 100%; /* 부모 높이를 초과하지 않도록 */
    border: none;
    border-radius: 0;
    overflow-y: auto; /* 세로 스크롤만 */
    overflow-x: auto; /* 가로 스크롤도 허용 */
    width: 100%;
    background: #ffffff;
}

/* data-table-compact 룩은 전역 _carbon-tables.scss 적용 */

/* 아이콘 */
.success-icon {
    color: #52b788;
    font-size: 16px;
}

.error-icon {
    color: #dc2626;
    font-size: 16px;
}

/* Phase 1.1: 경고 아이콘 */
.warning-icon {
    color: #f59e0b;
    font-size: 16px;
}

/* Scope 태그 */
.scope-tag {
    padding: 1px 4px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 600;
}

.scope-tag.scope-1 {
    background: #fef2f2;
    color: #dc2626;
}

.scope-tag.scope-2 {
    background: #fffbeb;
    color: #f59e0b;
}

.scope-tag.scope-3 {
    background: #f0f9ff;
    color: #0284c7;
}

/* 숫자 컬럼 */
.num-col {
    text-align: right;
}

/* 오류 컬럼 */
.error-col {
    max-width: 200px;
}

.error-text {
    font-size: 10px;
    color: #dc2626;
    line-height: 1.3;
}

/* 테이블 푸터 */
.table-footer-info {
    padding: 6px 16px;
    text-align: center;
    font-size: 11px;
    color: #64748b;
    background: #f8fafc;
    border: none;
    border-top: 1px solid #e2e8f0;
    border-radius: 0;
}

/* Phase 3: 액션 버튼 스타일 */
.action-col {
    text-align: center;
}

.action-buttons {
    display: flex;
    gap: 4px;
    justify-content: center;
    align-items: center;
}

.btn-action {
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
    background: transparent;
}

.btn-action:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

/* .btn-edit {
    color: #3b82f6;
} */

.btn-edit:hover:not(:disabled) {
    background: #dbeafe;
    color: #2563eb;
}

/* .btn-delete {
    color: #ef4444;
} */

.btn-delete:hover:not(:disabled) {
    background: #fee2e2;
    color: #dc2626;
}

/* Phase 4: UX 개선 스타일 */
.table-header-left {
    display: flex;
    gap: 16px;
    align-items: center;
    flex: 1;
}

.data-stats {
    display: inline-flex;
    gap: 6px;
    margin-left: 8px;
}

.filter-checkbox {
    display: flex;
    align-items: center;
}

.filter-checkbox label {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    cursor: pointer;
    font-size: 12px;
    color: #64748b;
}

.filter-checkbox input[type="checkbox"] {
    cursor: pointer;
}

.filter-label {
    display: flex;
    align-items: center;
    gap: 4px;
}

.error-messages {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.error-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.error-icon-small {
    font-size: 10px;
    color: #dc2626;
    flex-shrink: 0;
}

/* Phase 1.1: 경고 메시지 스타일 */
.warning-icon-small {
    font-size: 10px;
    color: #f59e0b;
    flex-shrink: 0;
}

.warning-messages {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.warning-item {
    display: flex;
    align-items: center;
    gap: 4px;
}

.warning-text {
    font-size: 11px;
    color: #f59e0b;
    line-height: 1.3;
}

.filter-badge-inline {
    display: inline-block;
    margin-left: 6px;
    padding: 2px 8px;
    background: #fef3c7;
    color: #92400e;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid #fbbf24;
}

/* 툴팁 내용 스타일 */
.tooltip-content {
    text-align: left;
    line-height: 1.5;
}

.tooltip-content small {
    font-size: 11px;
    opacity: 0.9;
}

/* Phase 3.1: 업로드 결과 모달 스타일 */
.upload-result-modal {
    padding: 0;
}

.result-summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.result-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    border-radius: 8px;
    border: 2px solid;
}

.result-card.success {
    background: #f0fdf4;
    border-color: #52b788;
}

.result-card.info {
    background: #eff6ff;
    border-color: #3b82f6;
}

.result-card.error {
    background: #fef2f2;
    border-color: #dc2626;
}

.result-card-icon {
    font-size: 28px;
}

.result-card.success .result-card-icon {
    color: #52b788;
}

.result-card.info .result-card-icon {
    color: #3b82f6;
}

.result-card.error .result-card-icon {
    color: #dc2626;
}

.result-card-content {
    flex: 1;
}

.result-card-label {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 4px;
}

.result-card-value {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
}

.result-section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 12px;
}

.result-section-title i {
    color: #f59e0b;
}

.result-errors {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
}

.error-list {
    max-height: 300px;
    overflow-y: auto;
}

.error-list-item {
    padding: 12px;
    background: #fef2f2;
    border-left: 3px solid #dc2626;
    border-radius: 4px;
    margin-bottom: 8px;
}

.error-list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.error-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background: #dc2626;
    color: #ffffff;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 600;
}

.error-message {
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: #dc2626;
}

.error-data {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 11px;
    color: #64748b;
}

.error-data span {
    padding: 2px 8px;
    background: #ffffff;
    border-radius: 4px;
}

.error-more {
    padding: 8px;
    text-align: center;
    font-size: 12px;
    color: #64748b;
    font-style: italic;
}

.result-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    font-size: 13px;
    color: #475569;
    margin-top: 24px;
}

.result-info i {
    color: #3b82f6;
}

.modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 16px;
    margin-top: 16px;
    border-top: 1px solid #e2e8f0;
}
</style>
